import React, { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import { FireParticles, ElectricWire } from "@/components/scene/Hazards";
import ToonNPC, { NPC_PALETTE } from "@/components/scene/ToonNPC";
import VolumetricSmoke from "@/components/scene/VolumetricSmoke";
import Avatar from "@/components/scene/Avatar";

const SEG_H = 2.4; // vertical spacing per floor segment
const N = 9; // number of cycling segments
const CYCLE = N * SEG_H;

function StairSegment({ yRef, index }) {
  const wallMat = useMemo(
    () => new THREE.MeshStandardMaterial({ color: 0x2a2c31, roughness: 0.85, metalness: 0.15 }),
    []
  );
  const stepMat = useMemo(
    () => new THREE.MeshStandardMaterial({ color: 0x3a3d43, roughness: 0.7, metalness: 0.2 }),
    []
  );
  const railMat = useMemo(
    () => new THREE.MeshStandardMaterial({ color: 0x1c1e22, roughness: 0.4, metalness: 0.6 }),
    []
  );
  const steps = [0, 1, 2, 3, 4];
  return (
    <group ref={yRef}>
      {/* side walls */}
      <mesh material={wallMat} position={[-2.1, 0, -1.5]}>
        <boxGeometry args={[0.3, SEG_H, 5]} />
      </mesh>
      <mesh material={wallMat} position={[2.1, 0, -1.5]}>
        <boxGeometry args={[0.3, SEG_H, 5]} />
      </mesh>
      {/* landing */}
      <mesh material={stepMat} position={[0, -SEG_H / 2 + 0.1, 0.8]}>
        <boxGeometry args={[3.6, 0.2, 1.4]} />
      </mesh>
      {/* flight of steps descending toward -z */}
      {steps.map((s) => (
        <mesh key={s} material={stepMat} position={[0, 0.5 - s * 0.35, -0.5 - s * 0.6]}>
          <boxGeometry args={[3.2, 0.22, 0.6]} />
        </mesh>
      ))}
      {/* railings */}
      <mesh material={railMat} position={[-1.5, 0.1, -1]} rotation={[0.5, 0, 0]}>
        <boxGeometry args={[0.08, 0.08, 4]} />
      </mesh>
      <mesh material={railMat} position={[1.5, 0.1, -1]} rotation={[0.5, 0, 0]}>
        <boxGeometry args={[0.08, 0.08, 4]} />
      </mesh>
      {/* emergency light */}
      <pointLight color={0x8fa9c4} intensity={0.5} distance={4} position={[0, SEG_H / 2 - 0.2, -1]} />
      <mesh position={[0, SEG_H / 2 - 0.2, -2.9]}>
        <boxGeometry args={[0.3, 0.12, 0.05]} />
        <meshStandardMaterial color={0x9fd8ff} emissive={0x3a6a8a} emissiveIntensity={1.2} />
      </mesh>
    </group>
  );
}

export default function StairwellScene({ game, audio }) {
  const camera = useThree((s) => s.camera);
  const segRefs = useRef([]);
  const scroll = useRef(0);
  const hazardCooldown = useRef(0);
  const flash = useRef();
  const flashTarget = useRef();
  const godray = useRef();
  const completed = useRef(false);

  // hazards fixed within the cycle (y positions)
  const hazards = useMemo(
    () => [
      { type: "fire", base: SEG_H * 2, x: -1.2 },
      { type: "wire", base: SEG_H * 4.5, x: 1.1 },
      { type: "fire", base: SEG_H * 6.5, x: 1.0 },
      { type: "wire", base: SEG_H * 8, x: -1.0 },
    ],
    []
  );
  const fireRefs = useRef([]);
  const wireRefs = useRef([]);
  const npcRefs = useRef([]);

  // toon extras descending alongside + a couple ascending (roof divergence)
  const npcs = useMemo(
    () => [
      { base: SEG_H * 1.5, x: -0.9, dir: "down", pal: 0 },
      { base: SEG_H * 3.5, x: 0.8, dir: "down", pal: 2 },
      { base: SEG_H * 5.5, x: -0.7, dir: "up", pal: 4 },
      { base: SEG_H * 7.5, x: 0.9, dir: "down", pal: 1 },
    ],
    []
  );

  useFrame((state, dt) => {
    const g = game.current;
    if (g.mode !== "stairwell") return;

    // descent progression
    if (g.descentActive && g.descentFloor > 0) {
      const rate = (81 / 26) * (g.running ? 1.75 : 1); // hold-to-run clears floors faster
      g.descentFloor = Math.max(0, g.descentFloor - dt * rate);
      scroll.current += dt * rate * SEG_H;
    }
    if (g.descentFloor <= 0 && !completed.current) {
      completed.current = true;
      g.descentActive = false;
      g.onDescentComplete && g.onDescentComplete();
    }

    // position cycling segments (move up as we descend)
    const off = scroll.current % CYCLE;
    for (let i = 0; i < N; i++) {
      const seg = segRefs.current[i];
      if (!seg) continue;
      let y = ((i * SEG_H + off) % CYCLE) - CYCLE / 2;
      seg.position.y = y;
    }

    // hazards positions + contact detection
    hazardCooldown.current -= dt;
    let fi = 0;
    let wi = 0;
    hazards.forEach((h) => {
      let y = ((h.base + off) % CYCLE) - CYCLE / 2;
      const ref = h.type === "fire" ? fireRefs.current[fi++] : wireRefs.current[wi++];
      if (ref) {
        ref.position.set(h.x, y, -1.2);
        ref.visible = y > -2 && y < 3.5;
      }
      // contact when hazard aligns with camera plane during active descent
      if (g.descentActive && Math.abs(y) < 0.5 && hazardCooldown.current <= 0) {
        hazardCooldown.current = 1.1;
        audio && audio.hazardZap();
        g.onHazard && g.onHazard(h.type);
      }
    });

    // toon crowd cycling through the shaft
    npcs.forEach((npc, i) => {
      let y = ((npc.base + off) % CYCLE) - CYCLE / 2;
      const ref = npcRefs.current[i];
      if (ref) {
        ref.position.set(npc.x, y - 1.0, -1.1);
        ref.visible = y > -2.5 && y < 3.5;
      }
    });

    // camera head-bob first-person + 360° free-look (base downward tilt down the shaft)
    const t = state.clock.elapsedTime;
    const bob = g.descentActive ? Math.sin(t * 8) * 0.03 : 0;
    const ox = g.camOffX || 0, oz = g.camOffZ || 0;
    const yaw = g.lookYaw || 0;
    if (g.pov === "brian") {
      // Over-the-shoulder chase cam — sits above & behind so Stanley reads clearly
      // ahead alongside Brian, instead of being tucked "behind" the first-person eye.
      const px = 0.55 + ox, py = 0.8 + bob, pz = 0.7 + oz;
      camera.position.set(px, py, pz);
      const pitch = (g.lookPitch || 0) - 0.3;
      const cp = Math.cos(pitch);
      camera.lookAt(px + Math.sin(yaw) * cp * 4, py + Math.sin(pitch) * 4, pz - Math.cos(yaw) * cp * 4);
    } else {
      const px = ox, py = 0.4 + bob, pz = 1.8 + oz;
      camera.position.set(px, py, pz);
      const pitch = (g.lookPitch || 0) - 0.42; // bias the gaze down the descending shaft
      const cp = Math.cos(pitch);
      camera.lookAt(px + Math.sin(yaw) * cp * 4, py + Math.sin(pitch) * 4, pz - Math.cos(yaw) * cp * 4);
    }

    // flashlight following camera + volumetric god-ray beacon
    if (flash.current) {
      flash.current.position.copy(camera.position);
      if (flashTarget.current) {
        flashTarget.current.position.set(0, -2, -4);
        flash.current.target = flashTarget.current;
      }
    }
    if (godray.current) {
      const dir = new THREE.Vector3(0, -2, -4).sub(camera.position).normalize();
      godray.current.position.copy(camera.position).addScaledVector(dir, 5.5);
      godray.current.quaternion.setFromUnitVectors(new THREE.Vector3(0, -1, 0), dir);
      const flick = 0.85 + Math.sin(state.clock.elapsedTime * 9) * 0.06;
      if (godray.current.material) godray.current.material.opacity = 0.11 * flick;
    }
  });

  return (
    <group>
      <fogExp2 attach="fog" args={[0x0a0b0d, 0.11]} />
      <ambientLight color={0x232833} intensity={0.72} />

      {new Array(N).fill(0).map((_, i) => (
        <StairSegment key={i} index={i} yRef={(el) => (segRefs.current[i] = el)} />
      ))}

      {/* dusty rubble on the landing + lighting-reactive volumetric smoke filling the shaft */}
      {new Array(9).fill(0).map((_, i) => (
        <mesh key={`r${i}`} position={[(Math.random() - 0.5) * 3, 0.05 + Math.random() * 0.15, 0.4 - Math.random() * 1.8]} rotation={[Math.random(), Math.random(), Math.random()]}>
          <boxGeometry args={[0.15 + Math.random() * 0.3, 0.08 + Math.random() * 0.12, 0.15 + Math.random() * 0.25]} />
          <meshStandardMaterial color={0x6a655c} roughness={1} />
        </mesh>
      ))}
      <VolumetricSmoke count={20} area={[3.4, 4, 3]} center={[0, 0, -1.2]} color={0x33302a} opacity={0.5} rise={0.2} getIntensity={() => (game.current.smokeDensity != null ? game.current.smokeDensity : 0.65)} clearPoint={[-0.1, -0.7, -1.4]} clearRadius={3.2} />

      {/* green clearance vector guide down the shaft — brightened to guide through smoke */}
      <mesh position={[0, 0, -1]}>
        <cylinderGeometry args={[0.035, 0.035, CYCLE, 8]} />
        <meshBasicMaterial color={0x66ffa6} transparent opacity={0.85} toneMapped={false} />
      </mesh>
      <mesh position={[0, 0, -1]}>
        <cylinderGeometry args={[0.11, 0.11, CYCLE, 8]} />
        <meshBasicMaterial color={0x2fae5f} transparent opacity={0.22} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* High-contrast glowing EXIT signs + green path chevrons down the stairwell */}
      {[1.6, -0.4, -2.4].map((y, i) => (
        <group key={`exit${i}`} position={[1.45, y, -1.4]}>
          <pointLight color={0x39ff88} intensity={1.6} distance={4} />
          <mesh>
            <boxGeometry args={[0.62, 0.24, 0.06]} />
            <meshBasicMaterial color={0x0b2f18} toneMapped={false} />
          </mesh>
          <mesh position={[0, 0, 0.04]}>
            <boxGeometry args={[0.56, 0.18, 0.02]} />
            <meshBasicMaterial color={0x2bff86} toneMapped={false} />
          </mesh>
        </group>
      ))}
      {/* pulsing green floor chevrons pointing to the descent */}
      {[0.6, -0.9, -2.4].map((y, i) => (
        <mesh key={`chev${i}`} position={[0, y - 1.1, -0.2]} rotation={[-Math.PI / 2, 0, Math.PI / 4]}>
          <ringGeometry args={[0.12, 0.2, 4, 1, 0, Math.PI / 2]} />
          <meshBasicMaterial color={0x66ffa6} transparent opacity={0.85} toneMapped={false} side={2} />
        </mesh>
      ))}

      {/* hazards */}
      {hazards.filter((h) => h.type === "fire").map((h, i) => (
        <group key={`f${i}`} ref={(el) => (fireRefs.current[i] = el)}>
          <FireParticles count={60} scale={1.0} intensity={1} />
        </group>
      ))}
      {hazards.filter((h) => h.type === "wire").map((h, i) => (
        <group key={`w${i}`} ref={(el) => (wireRefs.current[i] = el)}>
          <ElectricWire />
        </group>
      ))}

      {/* toon crowd on the stairs */}
      {npcs.map((npc, i) => (
        <group key={`n${i}`} ref={(el) => (npcRefs.current[i] = el)}>
          <ToonNPC
            pose={npc.dir === "up" ? "climb" : "walk"}
            rotation={[0, npc.dir === "up" ? 0 : Math.PI, 0]}
            speed={5.5}
            {...NPC_PALETTE[npc.pal]}
          />
        </group>
      ))}

      {/* Both survivors are present for EVERY POV — Brian leads with his light,
          Stanley descends alongside. Gold rim + fill lights keep them silhouetted. */}
      <group position={[0.4, -0.95, -1.7]} rotation={[0, Math.PI, 0]}>
        <Avatar which="brian" url="/models/brian.glb" faceSign={-1} rim rig={{ base: "/models/brian_rig.glb", run: "/models/brian_run.glb" }} getRunning={() => game.current.running} getMotion={() => (game.current.descentActive ? "walk" : "idle")} />
        <pointLight color={0xffe6b0} intensity={2.0} distance={6} position={[0, 1.2, -0.4]} />
        <pointLight color={0xc9a24b} intensity={1.5} distance={5} position={[0, 1.4, 1.0]} />
      </group>
      <group position={[-0.55, -0.7, -1.05]} rotation={[0, Math.PI, 0]}>
        <Avatar which="stanley" url="/models/stanley.glb" faceSign={-1} rim rig={{ base: "/models/stanley_rig.glb", run: "/models/stanley_rig.glb" }} getRunning={() => game.current.running} getMotion={() => (game.current.descentActive ? "walk" : "idle")} />
        <pointLight color={0xbcd0e6} intensity={1.2} distance={5} position={[0, 1.2, -0.4]} />
        <pointLight color={0xc9a24b} intensity={1.4} distance={4.5} position={[0, 1.4, 1.0]} />
      </group>

      {/* first-person flashlight — brighter navigational beacon */}
      <spotLight
        ref={flash}
        color={0xffe9a8}
        intensity={4.4}
        distance={22}
        angle={Math.PI / 5}
        penumbra={0.5}
        decay={1}
      />
      <object3D ref={flashTarget} />
      {/* volumetric 'god ray' cone from the flashlight */}
      <mesh ref={godray}>
        <coneGeometry args={[1.7, 11, 24, 1, true]} />
        <meshBasicMaterial color={0xffe6b0} transparent opacity={0.11} depthWrite={false} side={THREE.DoubleSide} blending={THREE.AdditiveBlending} toneMapped={false} />
      </mesh>
    </group>
  );
}
